<template>
	<article class="ad-card">
		<header class="ad-header">
			<div class="ad-avatar" aria-hidden="true">
				<img v-if="card.logo && !logoFailed" :src="card.logo" alt="" @error="logoFailed = true" />
				<span v-else>{{ card.name.slice(0, 1).toUpperCase() }}</span>
				<span v-if="card.status" class="ad-status-dot" :class="{ active: card.status === 'ACTIVE' }"></span>
			</div>
			<div class="ad-heading">
				<h2 :title="card.name">{{ card.name }}</h2>
				<p class="platform">{{ platform }}<span v-if="savedDate"> · Saved {{ savedDate }}</span></p>
			</div>
		</header>
		<div v-if="card.block && !failed" class="ad-media" @error.capture="onMediaError">
			<Renderer :block="card.block" :shared="true" />
		</div>
		<p v-else class="media-fallback" role="status">Preview unavailable. Open this ad in Swipekit to view its media.</p>
		<div v-if="card.title || card.copy" class="ad-copy" :class="{ expanded }">
			<strong v-if="card.title">{{ card.title }}</strong>
			<p v-if="card.copy">{{ card.copy }}</p>
			<button v-if="card.copy.length > 114" type="button" :aria-expanded="expanded" @click="expanded = !expanded">{{ expanded ? 'Show less' : 'Show more' }}</button>
		</div>
		<div v-if="card.stats.length || card.status || card.mediaType" class="ad-details">
			<div v-if="card.status || card.mediaType" class="ad-labels">
				<span v-if="card.status" class="ad-status" :class="{ active: card.status === 'ACTIVE' }">{{ card.status === 'ACTIVE' ? 'Active' : 'Inactive' }}</span>
				<span v-if="card.mediaType">{{ card.mediaType }}</span>
			</div>
			<div v-if="card.stats.length" class="ad-stats">
				<span v-for="stat in card.stats" :key="stat.name" :title="stat.name" :aria-label="stat.value.toLocaleString('en-US') + ' ' + stat.name">
					<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path :d="statIcons[stat.name]" /></svg>
					{{ stat.value.toLocaleString('en-US') }}
				</span>
			</div>
		</div>
		<footer>
			<div class="ad-actions">
				<button v-if="card.publicUrl" type="button" @click="openPublicAd">View public ad <span aria-hidden="true">↗</span></button>
				<button type="button" @click="openAd">Open in Swipekit <span aria-hidden="true">↗</span></button>
			</div>
			<p v-if="linkError" role="status">{{ linkError }}</p>
		</footer>
	</article>
</template>

<script>
import Renderer from '@swipekit/components/renderer/index.vue';

export default {
	components: { Renderer },
	props: ['card', 'bridge'],
	data: function () {
		return {
			failed: false, logoFailed: false, linkError: '', expanded: false,
			// Same symbols used by the app's item/metadata.vue.
			statIcons: {
				likes: 'M21 10C21 15.75 12.75 20 12 20C11.25 20 3 15.75 3 10C3 6 5.5 4 8 4C10.5 4 12 5.5 12 5.5C12 5.5 13.5 4 16 4C18.5 4 21 6 21 10Z',
				comments: 'M12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12C3 13.49 3.36 14.89 4 16.13L3 21L8 20.06C9.2 20.66 10.56 21 12 21Z',
				shares: 'M14.34 7.38L8.66 10.62M8.66 13.38L14.34 16.62M20 6A3 3 0 1 1 14 6A3 3 0 1 1 20 6M9 12A3 3 0 1 1 3 12A3 3 0 1 1 9 12M20 18A3 3 0 1 1 14 18A3 3 0 1 1 20 18',
				views: 'M9 5V19M3 11V19M21 8V19M15 14V19',
			},
		};
	},
	computed: {
		platform: function () {
			const names = { facebook: 'Facebook', instagram: 'Instagram', 'instagram-feed': 'Instagram', tiktok: 'TikTok', 'tiktok-top-ads': 'TikTok', 'tiktok-library': 'TikTok', linkedin: 'LinkedIn', pinterest: 'Pinterest', google: 'Google', 'upload-custom': 'Upload' };
			return names[this.card.platform] || this.card.platform;
		},
		savedDate: function () {
			if (!this.card.savedAt) { return ''; }
			const date = new Date(this.card.savedAt);
			if (isNaN(date.getTime())) { return ''; }
			return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
		},
	},
	beforeDestroy: function () {
		const videos = this.$el.querySelectorAll('video');
		for (const video of videos) { video.pause(); }
	},
	methods: {
		onMediaError: function (event) {
			if (['IMG', 'VIDEO', 'SOURCE'].includes(event.target.tagName)) { this.failed = true; }
		},
		openLink: async function (url) {
			this.linkError = '';
			try {
				const result = await this.bridge.openLink({ url });
				if (result.isError) { this.linkError = 'Link was not opened. You can try again.'; }
			} catch (error) {
				this.linkError = 'Could not open this link. Please try again.';
			}
		},
		openPublicAd: async function () { await this.openLink(this.card.publicUrl); },
		openAd: async function () { await this.openLink(this.card.url); },
	},
};
</script>
